import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Save, RefreshCw, History } from "lucide-react";

/** Admin/Super-Admin only. Edits the whitelisted basic-contact fields on a
 *  member. Every save is audit-logged with old/new snapshot + reason.
 */
const FIELDS = [
  { k: "name",     l: "Full name",  type: "text",  ph: "Full legal name" },
  { k: "email",    l: "Email",      type: "email", ph: "name@example.com" },
  { k: "phone",    l: "Phone",      type: "tel",   ph: "+91 98xxxxxxxx" },
  { k: "dob",      l: "Date of birth", type: "date" },
  { k: "gender",   l: "Gender",     type: "select", options: [
    { v: "", l: "—" }, { v: "male", l: "Male" }, { v: "female", l: "Female" }, { v: "other", l: "Other" },
  ]},
  { k: "pincode",  l: "PIN",        type: "text",  ph: "6-digit PIN" },
  { k: "state",    l: "State",      type: "text",  ph: "e.g. Bihar" },
  { k: "city",     l: "City",       type: "text",  ph: "e.g. Patna" },
  { k: "address",  l: "Address",    type: "textarea", ph: "Street, area, landmark" },
];

export default function EditMemberModal({ member, onClose, onSaved }) {
  const original = useMemo(() => {
    const o = {};
    FIELDS.forEach((f) => { o[f.k] = member?.[f.k] ?? ""; });
    return o;
  }, [member]);
  const [form, setForm] = useState(original);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);

  useEffect(() => { setForm(original); }, [original]);

  const diff = useMemo(() => {
    const d = {};
    FIELDS.forEach((f) => {
      const a = (original[f.k] ?? "");
      const b = (form[f.k] ?? "");
      if (String(a) !== String(b)) d[f.k] = { old: a, new: b };
    });
    return d;
  }, [form, original]);
  const changed = Object.keys(diff).length;

  const loadAudit = async () => {
    try {
      const { data } = await api.get(`/admin/users/${member.user_code}/audit?limit=50`);
      setAudit((data?.audit || []).filter((r) =>
        r.action === "member.profile.edit" || r.action === "member.sp.adjust"
      ));
      setShowAudit(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load audit");
    }
  };

  const save = async () => {
    if (!changed) { toast.info("No changes to save"); return; }
    if (reason.trim().length < 10) { toast.error("Reason must be at least 10 characters"); return; }
    if (!window.confirm(
      `Save ${changed} change${changed !== 1 ? "s" : ""} to ${member.user_code} — ${member.name}?\n\n` +
      Object.entries(diff).map(([k, v]) => `• ${k}: "${v.old || "—"}" → "${v.new || "—"}"`).join("\n") +
      `\n\nReason: ${reason.trim()}\n\nThis is audit-logged.`
    )) return;
    setBusy(true);
    try {
      const changes = {};
      Object.keys(diff).forEach((k) => { changes[k] = form[k]; });
      const { data } = await api.patch(`/admin/users/${member.user_code}/profile`, {
        changes, reason: reason.trim(),
      });
      toast.success(`Saved ${data.changed_fields} field${data.changed_fields !== 1 ? "s" : ""}`);
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-navy/40 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-member-modal"
        className="w-full max-w-3xl bg-white border border-border rounded-sm shadow-xl my-8"
      >
        <div className="flex items-start justify-between px-6 py-4 border-b border-border">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-navy/50">Edit Registration</div>
            <div className="text-navy text-lg mt-0.5">{member.user_code} · {member.name || "—"}</div>
            <div className="text-[11px] text-navy/50 mt-0.5">
              Role: <span className="uppercase">{member.role === "distributor" ? "IBA" : member.role}</span>
              {" · "}Joined {member.created_at ? new Date(member.created_at).toLocaleDateString() : "—"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="edit-member-audit-toggle" onClick={loadAudit}
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-2 py-1 border border-navy/30 text-navy/70 rounded-sm hover:bg-navy/5">
              <History size={11} /> Audit
            </button>
            <button onClick={onClose} data-testid="edit-member-close" className="p-1.5 text-navy/50 hover:text-navy">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {FIELDS.map((f) => {
              const changedThis = String(original[f.k] ?? "") !== String(form[f.k] ?? "");
              const border = changedThis ? "border-gold" : "border-line";
              return (
                <label key={f.k} className={f.type === "textarea" ? "md:col-span-2 block" : "block"}>
                  <span className="text-[10px] uppercase tracking-widest text-navy/50 flex items-center gap-1">
                    {f.l}
                    {changedThis && <span className="text-gold-deep">• modified</span>}
                  </span>
                  {f.type === "textarea" ? (
                    <textarea rows={2} value={form[f.k] ?? ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                      placeholder={f.ph} data-testid={`edit-member-${f.k}`}
                      className={`w-full mt-1 border ${border} rounded-sm px-3 py-2 text-sm focus:border-royal outline-none`} />
                  ) : f.type === "select" ? (
                    <select value={form[f.k] ?? ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                      data-testid={`edit-member-${f.k}`}
                      className={`w-full mt-1 border ${border} rounded-sm px-2 py-1.5 text-sm focus:border-royal outline-none bg-white`}>
                      {f.options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                  ) : (
                    <input type={f.type} value={form[f.k] ?? ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                      placeholder={f.ph} data-testid={`edit-member-${f.k}`}
                      className={`w-full mt-1 border ${border} rounded-sm px-2 py-1.5 text-sm focus:border-royal outline-none`} />
                  )}
                  {changedThis && (
                    <div className="mt-1 text-[10px] text-navy/50">
                      was: <span className="text-navy/70 italic">"{String(original[f.k] ?? "—")}"</span>
                    </div>
                  )}
                </label>
              );
            })}
          </div>

          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-navy/50">
              Reason (min 10 chars — permanently audit-logged)
            </span>
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Member requested email correction via support ticket #4321"
              data-testid="edit-member-reason"
              className="w-full mt-1 border border-line rounded-sm px-3 py-2 text-sm focus:border-royal outline-none" />
          </label>

          {showAudit && (
            <div className="border border-line rounded-sm bg-cream/40 p-3 max-h-60 overflow-y-auto" data-testid="edit-member-audit-list">
              <div className="text-[10px] uppercase tracking-widest text-navy/50 mb-2">Recent changes on this member</div>
              {audit.length === 0 && <div className="text-xs text-navy/50 italic">No previous changes.</div>}
              {audit.map((r) => (
                <div key={r.id} className="text-xs py-1.5 border-b border-line/50 last:border-0">
                  <div className="flex justify-between text-[10px] text-navy/50">
                    <span>{r.action.replace("member.", "").replace(".", " › ")}</span>
                    <span>{new Date(r.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-navy/70 mt-0.5">
                    by <b>{r.actor_user_code}</b>{r.meta?.reason ? ` · ${r.meta.reason}` : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-border flex items-center justify-between bg-cream/30">
          <div className="text-[11px] text-navy/60">
            {changed === 0 ? "No changes yet." : `${changed} field${changed !== 1 ? "s" : ""} modified.`}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} data-testid="edit-member-cancel"
              className="text-[11px] uppercase tracking-widest px-3 py-2 border border-line text-navy/70 rounded-sm hover:bg-navy/5">
              Cancel
            </button>
            <button onClick={save} disabled={busy || !changed} data-testid="edit-member-save"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-4 py-2 bg-royal text-white hover:bg-royal-deep rounded-sm disabled:opacity-50">
              {busy ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
